import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Card, CardContent, CardActions,
  Button, Chip, Avatar, Divider, CircularProgress, Alert,
  Stack, LinearProgress,
} from '@mui/material';
import {
  Work as WorkIcon,
  People as PeopleIcon,
  Event as EventIcon,
  LocalOffer as LocalOfferIcon,
  TrendingUp as TrendingUpIcon,
  Add as AddIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { hasPermission } from '../../../../utils/permissionUtils';
import {
  APPLICANT_STATUSES, APPLICANT_STATUS_COLORS,
  formatEnumLabel,
} from '../../../../constants/hrm';
import { useRecruitmentDashboard } from '../../../../hooks/useHRM';

const PIPELINE_STAGES = APPLICANT_STATUSES
  .filter((s) => ['APPLIED', 'SCREENING', 'SHORTLISTED', 'INTERVIEW', 'OFFERED'].includes(s))
  .map((s) => ({ key: s, label: formatEnumLabel(s), color: APPLICANT_STATUS_COLORS[s] }));

const RecruitmentDashboard = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();

  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);

  const { data: dashboardData, isLoading: loading } = useRecruitmentDashboard(chatbotId);
  const dashboard = dashboardData?.data || {};

  useEffect(() => {
    document.title = 'Recruitment | KlonIT Web Application';
    checkPermissions();
  }, []);

  const checkPermissions = async () => {
    try {
      const view = await hasPermission('hrm', 'view');
      const modify = await hasPermission('hrm', 'add|edit');
      setCanView(view);
      setCanModify(modify);
    } catch (err) {
      console.error('Error checking permissions:', err);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, mt: { xs: 2, sm: 10 } }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!canView) {
    return (
      <Alert severity="error" sx={{ mt: { xs: 2, sm: 10 }, mx: 3 }}>
        <strong>Access Denied</strong><br />
        You don't have permission to view the Recruitment module.
      </Alert>
    );
  }

  const {
    open_jobs = 0,
    total_applicants = 0,
    interviews_this_week = 0,
    pending_offers = 0,
    applicants_by_status = {},
    recent_applicants = [],
  } = dashboard;

  const cards = [
    {
      title: 'Open Jobs',
      description: 'Active job requisitions currently accepting applications from candidates.',
      icon: <WorkIcon sx={{ fontSize: 60, color: '#4caf50' }} />,
      count: open_jobs,
      link: `/chatbot/${chatbotId}/hrm/jobs`,
      color: '#4caf50',
    },
    {
      title: 'Applicants',
      description: 'Total applicants across all jobs tracked through the recruitment pipeline.',
      icon: <PeopleIcon sx={{ fontSize: 60, color: '#2196f3' }} />,
      count: total_applicants,
      link: `/chatbot/${chatbotId}/hrm/applicants`,
      color: '#2196f3',
    },
    {
      title: 'Interviews',
      description: 'Interviews scheduled this week including all stages and formats.',
      icon: <EventIcon sx={{ fontSize: 60, color: '#ff9800' }} />,
      count: interviews_this_week,
      link: `/chatbot/${chatbotId}/hrm/interviews`,
      color: '#ff9800',
    },
    {
      title: 'Pending Offers',
      description: 'Offers awaiting candidate response or management approval.',
      icon: <LocalOfferIcon sx={{ fontSize: 60, color: '#9c27b0' }} />,
      count: pending_offers,
      link: `/chatbot/${chatbotId}/hrm/offers`,
      color: '#9c27b0',
    },
  ];

  const totalPipeline = Object.values(applicants_by_status).reduce((a, b) => a + b, 0);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            <TrendingUpIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Recruitment
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Talent Acquisition & Applicant Tracking
          </Typography>
        </Box>
      </Box>

      {/* Main Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {cards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: '0.3s',
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-4px)',
                },
              }}
              onClick={() => navigate(card.link)}
            >
              <CardContent sx={{ flexGrow: 1, textAlign: 'center', pt: 4 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: `${card.color}15`,
                    margin: '0 auto 16px',
                  }}
                >
                  {card.icon}
                </Avatar>
                <Typography variant="h5" gutterBottom fontWeight={600}>
                  {card.title}
                </Typography>
                <Typography variant="h3" color={card.color} fontWeight={700} sx={{ mb: 2 }}>
                  {card.count.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {card.description}
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  sx={{ bgcolor: card.color }}
                >
                  Manage
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Applicant Pipeline and Recent Applicants */}
      <Grid container spacing={3}>
        {/* Applicant Pipeline */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Applicant Pipeline
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {totalPipeline > 0 ? (
              <Stack spacing={2}>
                {PIPELINE_STAGES.map((stage) => {
                  const count = applicants_by_status[stage.key] || 0;
                  const percentage = totalPipeline > 0 ? (count / totalPipeline) * 100 : 0;

                  return (
                    <Box key={stage.key} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ minWidth: 100 }}>
                        <Chip
                          label={stage.label}
                          size="small"
                          sx={{
                            backgroundColor: stage.color,
                            color: '#fff',
                            fontWeight: 'bold',
                            minWidth: 90,
                          }}
                        />
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={percentage}
                          sx={{
                            height: 20,
                            borderRadius: 10,
                            backgroundColor: `${stage.color}20`,
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: stage.color,
                              borderRadius: 10,
                            },
                          }}
                        />
                      </Box>
                      <Box sx={{ minWidth: 80, textAlign: 'right' }}>
                        <Typography variant="body2" fontWeight="bold">
                          {count} ({percentage.toFixed(0)}%)
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No applicants in the pipeline yet
              </Typography>
            )}

            {canModify && (
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => navigate(`/chatbot/${chatbotId}/hrm/jobs?action=new`)}
                >
                  Post New Job
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Recent Applicants */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 1 }}>
              <Typography variant="h6" fontWeight={600}>
                Recent Applicants
              </Typography>
              <Button
                size="small"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate(`/chatbot/${chatbotId}/hrm/applicants`)}
              >
                View All
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {recent_applicants.length > 0 ? (
              <Stack spacing={2}>
                {recent_applicants.slice(0, 5).map((applicant, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 2,
                      p: 1.5,
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                    }}
                  >
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light' }}>
                      {(applicant.first_name || '?')[0]}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle2">
                        {applicant.first_name} {applicant.last_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {applicant.job_title || 'Unknown Position'} •{' '}
                        {applicant.applied_date
                          ? new Date(applicant.applied_date).toLocaleDateString()
                          : '-'}
                      </Typography>
                    </Box>
                    <Chip
                      label={formatEnumLabel(applicant.status) || 'Applied'}
                      size="small"
                      sx={{
                        backgroundColor: APPLICANT_STATUS_COLORS[applicant.status] || '#9e9e9e',
                        color: '#fff',
                        fontWeight: 'bold',
                      }}
                    />
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No recent applicants
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      {canModify && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom fontWeight={600}>
            Quick Actions
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Button
              variant="contained"
              startIcon={<WorkIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/hrm/jobs?action=new`)}
            >
              Post New Job
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<PeopleIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/hrm/applicants`)}
            >
              View Applicants
            </Button>
            <Button
              variant="contained"
              color="warning"
              startIcon={<EventIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/hrm/interviews`)}
            >
              Schedule Interview
            </Button>
            <Button
              variant="outlined"
              startIcon={<LocalOfferIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/hrm/offers`)}
            >
              Manage Offers
            </Button>
          </Stack>
        </Paper>
      )}
    </Box>
  );
};

export default RecruitmentDashboard;
